/* Copyright 2011-2018 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/dom-construct",
    "dijit/layout/ContentPane",
    "dijit/registry",
    "dojo/query",
    "dojo/dom-geometry",
    "MW/toolstrip/widgets/_WidgetMixin",
    "MW/toolstrip/mixins/property/RemovableMixin"
], function (declare, domConstruct, ContentPane, registry, query, domGeom, _WidgetMixin, RemovableMixin) {

    function sectionSorter(a, b) {
        var result = 0;

        if (a.collapsePriority > b.collapsePriority) {
            result = -1;
        } else if (a.collapsePriority < b.collapsePriority) {
            result = 1;
        }

        return result;
    };

    function matchesSelector (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
        var matchesMethod;

        if (!element || !selector) {
            return false;
        }

        matchesMethod = 
            Element.prototype.matches || 
            Element.prototype.matchesSelector || 
            Element.prototype.mozMatchesSelector ||
            Element.prototype.msMatchesSelector || 
            Element.prototype.oMatchesSelector || 
            Element.prototype.webkitMatchesSelector ||
            function(s) {
                var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                    i = matches.length;
                while (--i >= 0 && matches.item(i) !== this) {}
                return i > -1;            
            };

        return matchesMethod.call(element, selector);
    };

    return declare([ContentPane, _WidgetMixin, RemovableMixin], {
        /** @lends Tab# */

        /**
         * Tab is a Container which can have Sections as it's children.
         * @extends dijit/layout/ContentPane
         * @constructs
         * @param args
         * <li>title: The text to be displayed in the title of the tab.</li>
         */
        constructor: function () {
            this['class'] = "tabContentPane";
        },

        /**
         * @override
         */
        postCreate: function () {
            // Using span here instead of div, since spans grow and shrink based on the contents and that facilitates
            // the calculation of sum of widths of sections when resizing the Toolstrip.
            this.containerNode = domConstruct.create("span", {'class': "sectionContainer"}, this.domNode);
        },

        resize: function () {
            var tabgroup = this.getParent(),
                toolstrip = tabgroup.getParent();

            if (!toolstrip._startupComplete) {
                return;
            }

            // Ignore any resize calls until a final tab selection is made. One example is when we are in equation tab 
            // and click in the live editor window. The live editor tab group is removed by removing each individual tab 
            // within it and each remove triggers a resize call. Then the live editor tab group is added back and each
            // add triggers a resize call. In between apps and home tabs are being selected multiple times before the 
            // live editor tab is finally selected. (see geck g1458489)
            if (toolstrip._ignoreResize) {
                return;
            }

            var i, j, sections, numSections, containerNodeWidth, tabWidth, galleries, numGalleries,
                numShrunkGalleries = 0, numExpandedSections = 0;

            // Ignore any resize called after the Tab has already been destroyed. This can happen due to the
            // optimization of user interaction done in Toolstrip.js involving the use of setTimeout.
            if (this._destroyed) {
                return;
            }

            this.inherited(arguments);

            galleries = this._getAllDescendantGalleries();
            numGalleries = galleries.length;

            sections = this.getChildren();
            numSections = sections.length;

            // Sort the sections in decreasing order of collapse priority. Hence, the sections with lower collapse
            // priority will collapse first.
            sections.sort(sectionSorter);

            containerNodeWidth = this._getWidthOfAllSections();
            tabWidth = domGeom.position(this.domNode).w;

            // If the tab width is less than the sum of all the sections' width then we need to start collapsing.
            if (tabWidth < containerNodeWidth) {
                j = numGalleries - 1;

                // Starting from the right most gallery, start shrinking the galleries
                while (j >= 0) {
                    while (tabWidth < containerNodeWidth) {
                        if (!galleries[j].shrink()) {
                            numShrunkGalleries += 1;
                            break;
                        }
                        containerNodeWidth = this._getWidthOfAllSections();
                        tabWidth = domGeom.position(this.domNode).w;
                    }
                    j -= 1;
                }

                // If all the galleries in this tab have been shrunk then move on to collapsing of sections.
                if (numShrunkGalleries === numGalleries) {
                    // If the available tab width is not enough to contain all the sections then start hiding
                    // section's descendant labels starting from the right (they have already been sorted).
                    i = numSections - 1;
                    while ((i >= 0) && (tabWidth < containerNodeWidth)) {
                        sections[i].toggleDescendantLabels(false);
                        containerNodeWidth = this._getWidthOfAllSections();
                        tabWidth = domGeom.position(this.domNode).w;
                        i -= 1;
                    }

                    // If the available tab width is not enough to contain all the sections then start collapsing
                    // sections starting from the right (they have already been sorted).
                    i = numSections - 1;
                    while ((i >= 0) && (tabWidth < containerNodeWidth)) {
                        sections[i].collapse();
                        sections[i].toggleDescendantLabels(true);
                        containerNodeWidth = this._getWidthOfAllSections();
                        tabWidth = domGeom.position(this.domNode).w;
                        i -= 1;
                    }
                }
                // If the tab width is more than the sum of all the sections' width then we need to start expanding.
            } else {

                // If some sections are collapsed but we have enough tab width available then start expanding sections
                // starting from the left.
                i = 0;
                while ((i < numSections) && (tabWidth > containerNodeWidth)) {
                    sections[i].expand();
                    sections[i].toggleDescendantLabels(false);
                    containerNodeWidth = this._getWidthOfAllSections();
                    tabWidth = domGeom.position(this.domNode).w;
                    if (tabWidth < containerNodeWidth) {
                        sections[i].collapse();
                        break;
                    }
                    i += 1;
                }

                // If some section's descendant labels are collapsed but we have enough tab width available then start showing section
                // descendant labels starting from the left.
                i = 0;
                while ((i < numSections) && (tabWidth > containerNodeWidth)) {
                    sections[i].toggleDescendantLabels(true);
                    containerNodeWidth = this._getWidthOfAllSections();
                    tabWidth = domGeom.position(this.domNode).w;
                    if (tabWidth < containerNodeWidth) {
                        sections[i].toggleDescendantLabels(false);
                        break;
                    }
                    i += 1;
                }

                numExpandedSections = i;

                // If all the sections have been expanded then start growing any galleries in the tab.
                if (numExpandedSections === numSections) {

                    // Redo this since, the galleries might have moved back to the sections from their popups.
                    galleries = this._getAllDescendantGalleries();
                    numGalleries = galleries.length;

                    j = 0;
                    while (j < numGalleries) {
                        while (tabWidth > containerNodeWidth) {
                            if (galleries[j].grow()) {
                                containerNodeWidth = this._getWidthOfAllSections();
                                tabWidth = domGeom.position(this.domNode).w;
                                if (tabWidth < containerNodeWidth) {
                                    galleries[j].shrink();
                                    break;
                                }
                            } else {
                                break;
                            }
                        }
                        j += 1;
                    }
                }
            }
        },

        _getAllDescendantGalleries: function () {
            var galleryWidgets, galleryNodes = query(".gallery", this.domNode);

            galleryWidgets = galleryNodes.map(function (node) {
                return registry.byNode(node);
            });

            return galleryWidgets;
        },
        
        _getWidth: function () {
            return domGeom.position(this.domNode).w;
        },

        _getWidthOfAllSections: function () {
            var sections, numSections, index,
                widthOfSections = 0;

            sections = this.getChildren();
            numSections = sections.length;

            for (index = 0; index < numSections; index++) {
                widthOfSections += domGeom.position(sections[index].domNode).w
            }

            // Add 20 to account for the Toolstrip collapse button on the bottom right corner
            return widthOfSections + this._getCollapseButtonWidth();
        },

        _isStaticDisplayState: function () {
            // TODO: make this more robust.  We should likely have a local property.
            return this.domNode ? matchesSelector(this.domNode, '.staticToolstrip *') : null;
        },

        _isCompactDisplayMode: function () {
            // TODO: make this more robust.  We should likely have a local property.
            return this.domNode ? matchesSelector(this.domNode, '.compactToolstrip *') : null;
        },

        _getCollapseButtonWidth: function () {
            if (this._isStaticDisplayState()) {
                return 0;
            } else if (this._isCompactDisplayMode()) {
                return 50;
            }
            return 20;
        }
    });
});